<!-- Popup: TOC(Table of Contents) -->
<script lang="ts" setup>
import { watch } from 'vue'
import { useElementClick } from '@/composables/tracks'
import { useUserStore } from '@/stores'
import { useI18n } from '@/composables/useI18n'

const props = withDefaults(
  defineProps<{
    value: boolean
    book: any
    lockIconColor?: string
    restartMode?: boolean
    currentChapterId?: string
    unlockedChapterIds?: string[]
  }>(), {
    value: false,
    restartMode: false,
    currentChapterId: '',
    unlockedChapterIds: () => [],
  },
)

const emit = defineEmits<{
  (event: 'input', value: boolean): void
  (event: 'reset-chapter', chapterId: string | number, chapterNo: number): void
  (event: 'reset-all'): void
}>()

const userStore = useUserStore()
const { elementClick } = useElementClick()
const { t } = useI18n()

function isChapterLocked(item: any): boolean {
  const startPaidNo = props.book?.needPayChapterMinNo
    || props.book?.chapterChargeSummary?.startPaidChapterNo
    || 0
  if (!startPaidNo || item.sequenceNo < startPaidNo)
    return false
  if (userStore.subscribe)
    return false
  if (props.unlockedChapterIds?.includes(item.chapterId))
    return false
  return true
}

function isChapterCompleted(item: any): boolean {
  if (!props.currentChapterId || !props.book?.chapters)
    return false
  const cur = props.book.chapters.find((c: any) => c.chapterId === props.currentChapterId)
  return cur ? item.sequenceNo <= cur.sequenceNo : false
}

watch(
  () => props.value,
  (val) => {
    if (val)
      elementClick('catalogue')
  },
)
</script>

<template>
  <transition name="toc-fade">
    <div
      v-if="value && book"
      class="fixed inset-0 z-50 flex items-center justify-center"
      style="background-color: rgba(0, 0, 0, 0.8);"
      @click.self="emit('input', false)"
    >
      <div style="display: flex; flex-direction: column; align-items: center;">
        <!-- 白色卡片 -->
        <div
          style="position: relative; width: 315px; border-radius: 10px; overflow: hidden; background-color: #fff; display: flex; flex-direction: column; max-height: 65vh;"
        >
          <!-- 渐变标题头 -->
          <div
            class="toc-header"
            style="flex-shrink: 0; height: 44px; display: flex; align-items: center; justify-content: center; position: relative;"
          >
            <span style="color: #fff; font-size: 16px; font-family: Inter-Medium; font-weight: 500; white-space: nowrap; line-height: 12px;">
              {{ restartMode ? 'Chapter' : t('Catalogue') }}
            </span>
            <!-- 右上角重置图标：仅重置模式显示，点击触发整本重置确认 -->
            <img
              v-if="restartMode"
              src="/images/vowpath/coins/dialog-reset.webp"
              style="position: absolute; right: 16px; top: 50%; transform: translateY(-50%); width: 16px; height: 16px; cursor: pointer;"
              @click.stop="emit('reset-all')"
            >
          </div>
          <!-- 章节列表（可滚动） -->
          <ul
            class="toc-list"
            style="padding: 0 20px 17px 20px; flex: 1; min-height: 0; overflow-y: auto;"
          >
            <li
              v-for="(item, index) in book.chapters"
              :key="item.chapterId"
              style="display: flex; align-items: center; justify-content: space-between; min-height: 55px; border-bottom: 1px solid rgba(243, 245, 250, 1);"
            >
              <span
                class="truncate"
                :style="{
                  flex: '1',
                  color: item.chapterId === props.currentChapterId ? '#AC4CE2' : 'rgba(50, 53, 58, 1)',
                  fontSize: '14px',
                  fontFamily: 'Inter-Regular',
                  whiteSpace: 'nowrap',
                  lineHeight: '12px',
                  margin: '1px 0 0 0',
                }"
              >
                {{ item.chapterName }}
              </span>
              <div style="flex-shrink: 0; width: 14px; height: 14px; margin-left: 8px; display: flex; align-items: center; justify-content: center;">
                <template v-if="restartMode">
                  <img
                    v-if="isChapterLocked(item)"
                    src="/images/vowpath/coins/icon-lock.webp"
                    style="width: 14px; height: 14px;"
                  >
                  <img
                    v-else-if="isChapterCompleted(item)"
                    src="/images/vowpath/coins/reset-catalog.webp"
                    style="width: 14px; height: 14px; cursor: pointer;"
                    @click.stop="emit('reset-chapter', item.chapterId, index + 1)"
                  >
                </template>
                <template v-else>
                  <img
                    v-if="isChapterLocked(item)"
                    src="/images/vowpath/coins/icon-lock.webp"
                    style="width: 14px; height: 14px;"
                  >
                </template>
              </div>
            </li>
          </ul>
        </div>
        <!-- 关闭按钮 -->
        <img
          src="/images/vowpath/coins/btn-close.webp"
          style="width: 34px; height: 34px; margin-top: 16px; cursor: pointer;"
          @click="emit('input', false)"
        >
      </div>
    </div>
  </transition>
</template>

<style scoped>
.toc-fade-enter-active,
.toc-fade-leave-active {
  transition: opacity 0.25s ease;
}
.toc-fade-enter,
.toc-fade-leave-to {
  opacity: 0;
}
.toc-header {
  background: linear-gradient(270deg, rgba(255, 107, 210, 1) 0%, rgba(161, 98, 255, 1) 100%);
}
.toc-list::-webkit-scrollbar {
  width: 3px;
}
.toc-list::-webkit-scrollbar-track {
  background: transparent;
}
.toc-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
  border-radius: 3px;
}
</style>
